import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  JudgeMeBadge,
  JudgeMeReview,
  fetchJudgeMeBadge,
  fetchJudgeMeProductReviews,
} from './judgeme';
import { IconClose } from './icons';

interface Props {
  apiUrl: string;
  productId: string;
}

export function Stars({ rating, size = 14 }: { rating: number; size?: number }) {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return (
    <span className="il-jm-stars" aria-label={`${rating.toFixed(1)} stars`}>
      {Array.from({ length: 5 }, (_, i) => {
        const on = i < full || (i === full && half);
        return (
          <svg
            key={i}
            width={size}
            height={size}
            viewBox="0 0 24 24"
            className={on ? 'is-on' : 'is-off'}
            aria-hidden
          >
            <path d="M12 3.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8L12 16.9 6.8 19.6l1-5.8L3.6 9.7l5.8-.8L12 3.6z" />
          </svg>
        );
      })}
    </span>
  );
}

function formatDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function Chevron({ dir }: { dir: 'prev' | 'next' }) {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d={dir === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Full-screen lightbox for review photos (prev/next within the opened set). */
function ReviewImageViewer({
  pictures,
  index,
  onClose,
  onIndexChange,
}: {
  pictures: string[];
  index: number;
  onClose: () => void;
  onIndexChange: (next: number) => void;
}) {
  const total = pictures.length;
  const src = pictures[index];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && index > 0) onIndexChange(index - 1);
      if (e.key === 'ArrowRight' && index < total - 1) onIndexChange(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, total, onClose, onIndexChange]);

  if (!src || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="il-jm-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Review photo ${index + 1} of ${total}`}
      onClick={onClose}
    >
      <button
        type="button"
        className="il-jm-lightbox-close"
        aria-label="Close"
        onClick={onClose}
      >
        <IconClose size={22} />
      </button>

      {total > 1 && (
        <span className="il-jm-lightbox-count">
          {index + 1} / {total}
        </span>
      )}

      {index > 0 && (
        <button
          type="button"
          className="il-jm-lightbox-nav is-prev"
          aria-label="Previous photo"
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange(index - 1);
          }}
        >
          <Chevron dir="prev" />
        </button>
      )}

      {index < total - 1 && (
        <button
          type="button"
          className="il-jm-lightbox-nav is-next"
          aria-label="Next photo"
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange(index + 1);
          }}
        >
          <Chevron dir="next" />
        </button>
      )}

      <img
        className="il-jm-lightbox-img"
        src={src}
        alt=""
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body
  );
}

/** Compact stars + count (title row / bag list). */
export function JudgeMeSummary({ apiUrl, productId }: Props) {
  const [badge, setBadge] = useState<JudgeMeBadge | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchJudgeMeBadge(apiUrl, productId).then((b) => {
      if (cancelled) return;
      setBadge(b);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, productId]);

  if (loading) {
    return (
      <div className="il-jm il-jm-summary is-loading">
        <span className="il-jm-badge-text">…</span>
      </div>
    );
  }

  const count = badge?.numberOfReviews ?? 0;
  const rating = badge?.averageRating ?? 0;

  return (
    <div className="il-jm il-jm-summary">
      <Stars rating={rating} size={13} />
      <span className="il-jm-badge-text">
        {count > 0 ? (
          <>
            <strong>{rating.toFixed(1)}</strong>
            <span>({count})</span>
          </>
        ) : (
          <span>No reviews</span>
        )}
      </span>
    </div>
  );
}

/** Full review list — auto-loaded (all reviews from private API, cached). */
export function JudgeMeReviews({ apiUrl, productId }: Props) {
  const [badge, setBadge] = useState<JudgeMeBadge | null>(null);
  const [reviews, setReviews] = useState<JudgeMeReview[]>([]);
  const [busy, setBusy] = useState(true);
  const [viewer, setViewer] = useState<{ pictures: string[]; index: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setReviews([]);
    setViewer(null);
    fetchJudgeMeProductReviews(apiUrl, productId).then((data) => {
      if (cancelled) return;
      setBadge(data?.badge || null);
      setReviews(data?.reviews || []);
      setBusy(false);
    });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, productId]);

  const count = badge?.numberOfReviews ?? reviews.length;
  const rating = badge?.averageRating ?? 0;

  return (
    <section className="il-jm-section" aria-label="Customer reviews">
      <div className="il-jm-section-head">
        <h4 className="il-jm-section-title">Reviews</h4>
        {!busy && count > 0 && (
          <div className="il-jm-section-meta">
            <Stars rating={rating} size={14} />
            <strong>{rating.toFixed(1)}</strong>
            <span>
              · {count} review{count === 1 ? '' : 's'}
            </span>
          </div>
        )}
      </div>

      {busy && <p className="il-muted-dark">Loading reviews…</p>}

      {!busy && reviews.length === 0 && (
        <p className="il-muted-dark">No written reviews for this product yet.</p>
      )}

      {!busy &&
        reviews.map((r) => (
          <article key={r.id} className="il-jm-rev">
            <div className="il-jm-rev-head">
              <Stars rating={r.rating} size={12} />
              <span className="il-jm-rev-author">{r.author}</span>
              {r.verified ? <span className="il-jm-verified">Verified</span> : null}
              {r.createdAt ? (
                <span className="il-jm-rev-date">{formatDate(r.createdAt)}</span>
              ) : null}
            </div>
            {r.title ? <div className="il-jm-rev-title">{r.title}</div> : null}
            {r.body ? <div className="il-jm-rev-body">{r.body}</div> : null}
            {r.pictures.length > 0 && (
              <div className="il-jm-rev-pics">
                {r.pictures.map((src, i) => (
                  <button
                    key={src}
                    type="button"
                    className="il-jm-rev-pic"
                    aria-label={`View review photo ${i + 1}`}
                    onClick={() => setViewer({ pictures: r.pictures, index: i })}
                  >
                    <img src={src} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </article>
        ))}

      {viewer && (
        <ReviewImageViewer
          pictures={viewer.pictures}
          index={viewer.index}
          onClose={() => setViewer(null)}
          onIndexChange={(index) => setViewer((v) => (v ? { ...v, index } : v))}
        />
      )}
    </section>
  );
}
